import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { LargeDialog } from "@/components/LargeDialog";
import { getUserCompanies, type User } from "@/api/user.service";
import {
  listCategorias,
  localizarCategorias,
  type AcaoResponsavel,
  type Responsavel,
} from "@/api/metas.service";
import { useNotification } from "@/context/NotificationContext";
import {
  ARVORE_CATEGORIAS_VAZIA,
  RAIZ,
  alternar,
  incorporarFilhos,
  incorporarNos,
  parciais,
  soCategorias,
  type ArvoreCategorias,
} from "@/pages/panel/metasResponsaveisArvore";

/** Ações que atuam sobre um escopo — sem lojas e categorias não há o que calcular, revisar ou aprovar. */
export const ACOES_DE_ESCOPO: AcaoResponsavel[] = ["CALCULAR", "REVISAR", "APROVAR"];

/** Quantas listas de filhos vão ao ERP ao mesmo tempo ao marcar um pai. */
const CONCORRENCIA = 6;

interface Loja {
  codigo: string;
  nome: string;
}

interface MetasResponsavelModalProps {
  /** Ação à qual o usuário está sendo vinculado — null fecha o modal. */
  acao: { id: AcaoResponsavel; rotulo: string } | null;
  clientId: string;
  /** Usuários que podem ser escolhidos (quem tem o módulo Metas). */
  usuarios: User[];
  /** Permissões já cadastradas nesta ação, por usuário — cada ação tem o próprio escopo. */
  permissoes: Record<string, Responsavel>;
  /** Usuário em edição; null = adicionando alguém novo à ação. */
  editando: User | null;
  onClose: () => void;
  onSave: (responsavel: Responsavel) => Promise<void>;
}

// Vincula um usuário a uma ação. Para as ações de escopo (calcular, revisar,
// aprovar) pede lojas e categorias; para as demais só o usuário. O escopo gravado
// vale só para esta ação — as outras ações do usuário mantêm o delas.
export function MetasResponsavelModal({ acao, clientId, usuarios, permissoes, editando, onClose, onSave }: MetasResponsavelModalProps) {
  const { notify } = useNotification();

  const [usuario, setUsuario] = useState<User | null>(null);
  const [lojasDoUsuario, setLojasDoUsuario] = useState<Loja[]>([]);
  const [lojas, setLojas] = useState<string[]>([]);
  const [selecao, setSelecao] = useState<Set<string>>(new Set());
  const [arvore, setArvore] = useState<ArvoreCategorias>(ARVORE_CATEGORIAS_VAZIA);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [ocupados, setOcupados] = useState<Set<string>>(new Set());
  const [carregandoUsuario, setCarregandoUsuario] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Espelha `arvore` de forma síncrona: o carregamento recursivo dos descendentes
  // lê a árvore no meio de vários awaits, e o estado do React ainda estaria velho.
  const arvoreRef = useRef<ArvoreCategorias>(ARVORE_CATEGORIAS_VAZIA);
  const atualizarArvore = (nova: ArvoreCategorias) => {
    arvoreRef.current = nova;
    setArvore(nova);
  };
  // Cresce a cada usuário aberto: uma carga que termina depois da troca é descartada.
  const versao = useRef(0);

  const exigeEscopo = !!acao && ACOES_DE_ESCOPO.includes(acao.id);

  const carregarFilhos = async (pai: string) => {
    if (arvoreRef.current.filhos[pai]) return;
    const filhos = soCategorias(await listCategorias(clientId, pai === RAIZ ? null : pai));
    atualizarArvore(incorporarFilhos(arvoreRef.current, pai, filhos));
  };

  /** Todos os descendentes de `codigo`, carregando do ERP o que ainda não veio. */
  const descendentesDe = async (codigo: string): Promise<string[]> => {
    const encontrados: string[] = [];
    let nivel = [codigo];
    while (nivel.length > 0) {
      for (let i = 0; i < nivel.length; i += CONCORRENCIA) {
        await Promise.all(nivel.slice(i, i + CONCORRENCIA).map(carregarFilhos));
      }
      nivel = nivel.flatMap((id) => arvoreRef.current.filhos[id] ?? []);
      encontrados.push(...nivel);
    }
    return encontrados;
  };

  const escolherUsuario = async (escolhido: User | null, precisaEscopo: boolean) => {
    versao.current += 1;
    const minhaVersao = versao.current;
    setUsuario(escolhido);
    setExpandidos(new Set());
    atualizarArvore(ARVORE_CATEGORIAS_VAZIA);
    setSelecao(new Set());
    setLojas([]);
    setLojasDoUsuario([]);
    if (!escolhido || !precisaEscopo) return;

    const salvo = permissoes[escolhido.id];
    setCarregandoUsuario(true);
    try {
      const [empresas, raiz, localizadas] = await Promise.all([
        getUserCompanies(escolhido.id).catch(() => []),
        listCategorias(clientId, null),
        salvo?.categorias.length ? localizarCategorias(clientId, salvo.categorias) : Promise.resolve([]),
      ]);
      if (minhaVersao !== versao.current) return;
      setLojasDoUsuario(
        empresas
          .filter((c) => c.client_id === clientId && c.erp_code)
          .map((c) => ({ codigo: String(c.erp_code), nome: c.name })),
      );
      // Os nós salvos e os ancestrais deles entram primeiro, para o "parcial" já valer na abertura.
      let nova = incorporarNos(
        ARVORE_CATEGORIAS_VAZIA,
        localizadas.filter((l) => !l.folha),
      );
      nova = incorporarFilhos(nova, RAIZ, soCategorias(raiz));
      atualizarArvore(nova);
      setLojas(salvo?.lojas ?? []);
      setSelecao(new Set(salvo?.categorias ?? []));
    } catch {
      if (minhaVersao === versao.current) notify("Erro ao carregar o cadastro do responsável", "error");
    } finally {
      if (minhaVersao === versao.current) setCarregandoUsuario(false);
    }
  };

  // Abrir o modal reinicia o formulário; em edição o usuário já vem escolhido.
  useEffect(() => {
    if (!acao) return;
    escolherUsuario(editando, ACOES_DE_ESCOPO.includes(acao.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acao?.id, editando]);

  const marcarOcupado = (codigo: string, ocupado: boolean) =>
    setOcupados((atual) => {
      const proximo = new Set(atual);
      if (ocupado) proximo.add(codigo);
      else proximo.delete(codigo);
      return proximo;
    });

  const alternarExpansao = async (codigo: string) => {
    if (expandidos.has(codigo)) {
      setExpandidos((atual) => {
        const proximo = new Set(atual);
        proximo.delete(codigo);
        return proximo;
      });
      return;
    }
    const minhaVersao = versao.current;
    marcarOcupado(codigo, true);
    try {
      await carregarFilhos(codigo);
      if (minhaVersao !== versao.current) return;
      setExpandidos((atual) => new Set(atual).add(codigo));
    } catch {
      notify("Erro ao carregar as categorias", "error");
    } finally {
      marcarOcupado(codigo, false);
    }
  };

  const marcar = async (codigo: string, marcado: boolean) => {
    const minhaVersao = versao.current;
    marcarOcupado(codigo, true);
    try {
      const descendentes = await descendentesDe(codigo);
      if (minhaVersao !== versao.current) return;
      setSelecao((atual) => alternar(atual, arvoreRef.current, codigo, descendentes, marcado));
    } catch {
      notify("Erro ao carregar as categorias", "error");
    } finally {
      marcarOcupado(codigo, false);
    }
  };

  const marcarTodas = async () => {
    const minhaVersao = versao.current;
    marcarOcupado(RAIZ, true);
    try {
      const descendentes = await descendentesDe(RAIZ);
      if (minhaVersao !== versao.current) return;
      setSelecao(new Set(descendentes));
    } catch {
      notify("Erro ao carregar as categorias", "error");
    } finally {
      marcarOcupado(RAIZ, false);
    }
  };

  const parcial = useMemo(() => parciais(selecao, arvore), [selecao, arvore]);

  const problema = !usuario
    ? "Selecione o usuário."
    : exigeEscopo && lojas.length === 0
      ? "Selecione ao menos uma loja."
      : exigeEscopo && selecao.size === 0
        ? "Selecione ao menos uma categoria."
        : null;

  const salvar = async () => {
    if (!acao || !usuario || problema) return;
    setSalvando(true);
    try {
      await onSave({
        userId: usuario.id,
        acao: acao.id,
        lojas: exigeEscopo ? lojas : [],
        categorias: exigeEscopo ? [...selecao] : [],
      });
      onClose();
    } catch {
      // Erro já notificado por quem chama onSave — o modal fica aberto para repetir.
    } finally {
      setSalvando(false);
    }
  };

  const lojasOpcoes: Loja[] = [
    ...lojasDoUsuario,
    // Loja salva que o usuário não acessa mais continua visível, para dar para tirá-la.
    ...lojas.filter((c) => !lojasDoUsuario.some((l) => l.codigo === c)).map((c) => ({ codigo: c, nome: c })),
  ];
  const nomeLoja = (codigo: string) => lojasOpcoes.find((l) => l.codigo === codigo)?.nome ?? codigo;

  const renderNo = (codigo: string, profundidade: number) => {
    const no = arvore.nos[codigo];
    if (!no) return null;
    const marcado = selecao.has(codigo);
    const aberto = expandidos.has(codigo);
    const filhos = arvore.filhos[codigo];
    return (
      <Box key={codigo}>
        <Stack direction="row" alignItems="center" sx={{ pl: profundidade * 3 }}>
          <IconButton
            size="small"
            aria-label={aberto ? `Recolher ${no.nome}` : `Expandir ${no.nome}`}
            onClick={() => alternarExpansao(codigo)}
            disabled={ocupados.has(codigo)}
            sx={{ visibility: filhos && filhos.length === 0 ? "hidden" : "visible" }}
          >
            {ocupados.has(codigo) ? <CircularProgress size={16} /> : aberto ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
          <FormControlLabel
            label={no.nome}
            control={
              <Checkbox
                size="small"
                checked={marcado}
                indeterminate={!marcado && parcial.has(codigo)}
                disabled={ocupados.has(codigo)}
                onChange={(_, valor) => marcar(codigo, valor)}
              />
            }
          />
        </Stack>
        {aberto && filhos?.map((filho) => renderNo(filho, profundidade + 1))}
      </Box>
    );
  };

  return (
    <LargeDialog
      open={!!acao}
      title={`${editando ? "Editar" : "Adicionar"} usuário — ${acao?.rotulo ?? ""}`}
      onClose={onClose}
      size="medium"
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: 1 }}>
        <Stack spacing={3}>
          <Autocomplete
            options={editando ? usuarios : usuarios.filter((u) => !permissoes[u.id])}
            value={usuario}
            disabled={!!editando}
            onChange={(_, valor) => escolherUsuario(valor, exigeEscopo)}
            getOptionLabel={(u) => u.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, u) => (
              <li {...props} key={u.id}>
                <Stack>
                  <Typography>{u.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.email}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Usuário" />}
            sx={{ maxWidth: 480 }}
          />

          {!exigeEscopo && usuario && (
            <Alert severity="info">Esta ação não depende de loja nem de categoria.</Alert>
          )}

          {exigeEscopo && usuario && carregandoUsuario && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {exigeEscopo && usuario && !carregandoUsuario && (
            <>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Lojas
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={lojasOpcoes.map((l) => l.codigo)}
                  value={lojas}
                  onChange={(_, valor) => setLojas(valor)}
                  getOptionLabel={nomeLoja}
                  renderInput={(params) => (
                    <TextField {...params} label="Lojas sob responsabilidade" placeholder={lojas.length ? "" : "Selecione"} />
                  )}
                  noOptionsText="O usuário não tem lojas vinculadas"
                />
                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => setLojas(lojasDoUsuario.map((l) => l.codigo))}
                  disabled={lojasDoUsuario.length === 0}
                >
                  Selecionar todas
                </Button>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    Categorias ({selecao.size} marcadas)
                  </Typography>
                  <Button size="small" onClick={marcarTodas} disabled={ocupados.has(RAIZ)}>
                    Marcar todas
                  </Button>
                  <Button size="small" onClick={() => setSelecao(new Set())} disabled={selecao.size === 0}>
                    Limpar
                  </Button>
                </Stack>
                <Box sx={{ maxHeight: 360, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                  {(arvore.filhos[RAIZ] ?? []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      Nenhuma categoria encontrada.
                    </Typography>
                  ) : (
                    arvore.filhos[RAIZ]!.map((codigo) => renderNo(codigo, 0))
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Marcar uma categoria marca todas as de baixo; desmarque as que não fazem parte do escopo e o ancestral fica
                  parcialmente marcado.
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </Box>

      {problema && usuario && !carregandoUsuario && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {problema}
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={salvar} disabled={!!problema || salvando || carregandoUsuario}>
          {salvando ? "Salvando..." : "Salvar"}
        </Button>
      </Stack>
    </LargeDialog>
  );
}
